import React, { useState } from 'react';
import {
  Box, Typography, Chip, Paper, Stack, Divider, Button, Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import HandshakeIcon from '@mui/icons-material/Handshake';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import {
  CoordinatePatient, CoordinationStatus,
  DispositionType, ClinicalNeed, UrgencyLevel,
  STATUS_CHIP_STYLE, URGENCY_CHIP_STYLE, URGENCY_BORDER_COLOR,
} from '~/types/coordinate';
import { useCoordinateContext } from '~/hooks/coordinate/useCoordinateContext';
import InteractionControls from '~/components/coordinate/patient-detail/InteractionControls';
import ProviderMatchList from '~/components/coordinate/patient-detail/ProviderMatchList';
import CommitConfirmFlow from '~/components/coordinate/patient-detail/CommitConfirmFlow';
import EHRNoteDialog from '~/components/coordinate/patient-detail/EHRNoteDialog';

const CHIP_SX = { fontWeight: 600, fontSize: '0.68rem', height: 20 };

const DetailPanel: React.FC<{ patient: CoordinatePatient }> = ({ patient }) => {
  const navigate = useNavigate();
  const {
    runMatching,
    selectProvider,
    confirmProvider,
    simulateCapacityChange,
    sendReferral,
    simulateReferralResponse,
    updateDisposition,
    updateClinicalNeeds,
    updateUrgency,
  } = useCoordinateContext();

  const [ehrDialogOpen, setEhrDialogOpen] = useState(false);

  const { coordinationStatus, selectedProviderId, matchResults } = patient;
  const statusStyle = STATUS_CHIP_STYLE[coordinationStatus];
  const urgencyStyle = URGENCY_CHIP_STYLE[patient.urgency];
  const selectedProvider = matchResults?.find(m => m.provider.id === selectedProviderId)?.provider;

  const showProviderList = !['pending', 'awaiting_response', 'negotiating', 'accepted', 'declined'].includes(coordinationStatus);
  const showSendCTA = coordinationStatus === 'confirmed';
  const showPostSend = ['awaiting_response', 'negotiating', 'accepted', 'declined'].includes(coordinationStatus);

  return (
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="h6" fontWeight={700}>{patient.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            Age {patient.age} · DOB {patient.dob} · Admit {patient.admitDate} · LOS {patient.losTargetDays}d target
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
            <Chip label={statusStyle.label} size="small" sx={{ ...CHIP_SX, bgcolor: statusStyle.bgcolor, color: statusStyle.color }} />
            <Chip label={urgencyStyle.label} size="small" sx={{ ...CHIP_SX, bgcolor: urgencyStyle.bgcolor, color: urgencyStyle.color }} />
          </Stack>
        </Box>
        <Button
          size="small"
          variant="outlined"
          endIcon={<OpenInNewIcon sx={{ fontSize: '0.8rem' }} />}
          onClick={() => navigate(`/coordinate/patient/${patient.id}`)}
          sx={{ flexShrink: 0, ml: 2, fontSize: '0.75rem' }}
        >
          Full Detail
        </Button>
      </Stack>

      <Divider />

      <InteractionControls
        patient={patient}
        onDispositionChange={(d: DispositionType) => updateDisposition(patient.id, d)}
        onNeedsChange={(needs: ClinicalNeed[]) => updateClinicalNeeds(patient.id, needs)}
        onUrgencyChange={(u: UrgencyLevel) => updateUrgency(patient.id, u)}
        onRunMatching={() => runMatching(patient.id)}
      />

      {coordinationStatus === 'pending' && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Review the controls above, then click <strong>Run Matching</strong>.
          </Typography>
        </Box>
      )}

      <CommitConfirmFlow
        coordinationStatus={coordinationStatus}
        selectedProviderName={selectedProvider?.name}
      />

      {showProviderList && (
        <ProviderMatchList
          patient={patient}
          onSelectAndConfirm={(providerId) => {
            selectProvider(patient.id, providerId);
            confirmProvider(patient.id);
          }}
          onSimulateCapacityChange={() => simulateCapacityChange(patient.id)}
        />
      )}

      {showSendCTA && (
        <>
          <Divider />
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={() => sendReferral(patient.id)}
            >
              Send Referral to {selectedProvider?.name ?? 'Provider'}
            </Button>
          </Stack>
        </>
      )}

      {showPostSend && (
        <>
          {coordinationStatus === 'awaiting_response' && (
            <>
              <Alert severity="info" sx={{ mb: 1.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  Awaiting Response from {selectedProvider?.name ?? 'Provider'}
                </Typography>
                {patient.referralSentAt && (
                  <Typography variant="caption">
                    Sent {new Date(patient.referralSentAt).toLocaleString()}
                  </Typography>
                )}
              </Alert>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ThumbUpIcon />}
                  onClick={() => simulateReferralResponse(patient.id, 'accepted')}
                  sx={{ color: '#006e60', borderColor: '#00bda1', '&:hover': { borderColor: '#006e60', bgcolor: 'rgba(0,189,161,0.04)' } }}
                >
                  Simulate: Accepted
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<ThumbDownIcon />}
                  onClick={() => simulateReferralResponse(patient.id, 'declined')}
                >
                  Simulate: Declined
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<HandshakeIcon />}
                  onClick={() => simulateReferralResponse(patient.id, 'negotiating')}
                  sx={{ color: '#635f99', borderColor: 'rgba(99,95,153,0.5)', '&:hover': { borderColor: '#635f99', bgcolor: 'rgba(99,95,153,0.04)' } }}
                >
                  Simulate: Conditions
                </Button>
              </Stack>
            </>
          )}

          {coordinationStatus === 'accepted' && (
            <>
              <Alert severity="success" sx={{ mb: 1.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  Referral Accepted by {selectedProvider?.name ?? 'Provider'}
                </Typography>
              </Alert>
              <Button
                variant="outlined"
                size="small"
                startIcon={<NoteAddIcon />}
                onClick={() => setEhrDialogOpen(true)}
              >
                Write EHR Discharge Note
              </Button>
            </>
          )}

          {coordinationStatus === 'negotiating' && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight={600}>
                {patient.providerNegotiation?.providerName ?? 'Provider'} responded with conditions
              </Typography>
              <Typography variant="body2">
                Open the full detail view to review and resolve conditions.
              </Typography>
            </Alert>
          )}

          {coordinationStatus === 'declined' && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight={600}>
                Referral Declined by {selectedProvider?.name ?? 'Provider'}.
              </Typography>
              <Typography variant="body2">
                Run matching again above to find alternatives.
              </Typography>
            </Alert>
          )}
        </>
      )}

      {selectedProvider && (
        <EHRNoteDialog
          open={ehrDialogOpen}
          onClose={() => setEhrDialogOpen(false)}
          patient={patient}
          providerName={selectedProvider.name}
        />
      )}
    </Box>
  );
};

const ListItem: React.FC<{ patient: CoordinatePatient; selected: boolean; onClick: () => void }> = ({
  patient, selected, onClick,
}) => {
  const s = STATUS_CHIP_STYLE[patient.coordinationStatus];
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2, py: 1.25,
        cursor: 'pointer',
        borderBottom: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: selected ? 'primary.main' : URGENCY_BORDER_COLOR[patient.urgency],
        bgcolor: selected ? 'primary.50' : 'transparent',
        '&:hover': { bgcolor: selected ? 'primary.50' : 'action.hover' },
        transition: 'background-color 0.1s',
      }}
    >
      <Typography variant="body2" fontWeight={selected ? 600 : 500} noWrap>{patient.name}</Typography>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.25 }}>
        <Chip label={s.label} size="small" sx={{ bgcolor: s.bgcolor, color: s.color, fontWeight: 600, fontSize: '0.65rem', height: 18 }} />
        <Typography variant="caption" color="text.disabled">{patient.facilityName}</Typography>
      </Stack>
    </Box>
  );
};

interface Props {
  patients: CoordinatePatient[];
}

const CoordinateSplitView: React.FC<Props> = ({ patients }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = patients.find(p => p.id === selectedId) ?? patients[0] ?? null;

  return (
    <Box sx={{ display: 'flex', gap: 2, minHeight: 0, flex: 1 }}>
      <Paper variant="outlined" sx={{ flex: '0 0 280px', overflowY: 'auto', borderRadius: 1 }}>
        {patients.length === 0 ? (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', p: 2 }}>
            No patients
          </Typography>
        ) : (
          patients.map(p => (
            <ListItem
              key={p.id}
              patient={p}
              selected={p.id === selected?.id}
              onClick={() => setSelectedId(p.id)}
            />
          ))
        )}
      </Paper>

      <Paper variant="outlined" sx={{ flex: 1, overflowY: 'auto', borderRadius: 1 }}>
        {selected ? (
          <DetailPanel patient={selected} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.disabled">Select a patient to begin</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default CoordinateSplitView;
